/**
 * PATH: packages/email-hub/src/feature/reader-header/lanes/laneSummary.ts
 * FILE_ID: EMAILHUB-READERHEADER-LANES-SUMMARY-001
 * PURPOSE: Reduce each metadata lane to the one value its collapsed chip shows.
 * ROLE IN ARCHITECTURE: Pure selector feeding CompactLaneChips. The lanes
 *   themselves are unchanged; this only decides what to print on the closed
 *   chip before the operator opens the lane.
 * MAIN EXPORTS: buildLaneChipSummaries, LaneChipSummary.
 * NON-RESPONSIBILITIES: No rendering, no hooks, no network. It does NOT
 *   re-derive identity or signal rules — resolveIdentitySummary and
 *   selectIntelligenceSignals stay the single source for those, and the
 *   category label is resolved by the caller with getCategoryConfig so this
 *   file never forks the category registry.
 *
 * WHY: At compact width the four lanes stack, so the resting header spends
 *   ~4 rows restating metadata before the message body starts. Collapsing
 *   each lane to one chip costs one row, but only works if the closed chip
 *   still answers the question the lane answers — "filed where?", "who from?"
 *   — otherwise the operator must open all four to learn anything.
 */
import { getCategoryConfig } from '../../lib/categoryConfig'
import {
  useOrgCustomCategories,
  usePersonalCategories,
} from '../../hooks/useEmailLabelPrefs'
import type { Email } from '../../hooks/useEmails'
import { resolveIdentitySummary, selectIntelligenceSignals } from './laneModel'
import type { ReaderTriageLaneId } from '../menus/menuScopes'

export interface LaneChipSummary {
  lane: ReaderTriageLaneId
  /** Group caption, matching the lane's short label. */
  label: string
  /**
   * Current value, or null when the group has nothing set. Null renders the
   * bare caption — an empty group must still be openable, because "not filed
   * yet" is exactly when the operator needs the filing controls.
   */
  value: string | null
  /** Items behind the value, rendered as "+N". Never negative. */
  extra: number
}

/** Inputs the caller resolves with the same helpers the open lanes use. */
export interface LaneSummaryContext {
  /** getCategoryConfig(...).label, or null when the email is uncategorised. */
  categoryLabel: string | null
  /** Undismissed verification code present (lane state, not raw email). */
  hasVerification: boolean
  hasInvoice: boolean
  /** Placements still awaiting review — the Filed lane's "+N to review". */
  reviewCount: number
}

/** Sender fallback when CRM has no company for the address. */
function senderFallback(email: Email): string | null {
  const name = email.from_name?.trim()
  if (name) return name
  const address = email.from_address?.trim()
  if (!address) return null
  // Local part only: a full address never fits a collapsed chip, and the
  // domain is the part the Company lane would have shown as the company.
  return address.split('@')[0] || address
}

function companySummary(email: Email): Pick<LaneChipSummary, 'value' | 'extra'> {
  const identity = resolveIdentitySummary(email)
  return {
    value: email.company_label?.trim() || senderFallback(email),
    // roles beyond the first — the same "+N roles" the open lane offers.
    extra: Math.max(0, identity.roles.length - 1),
  }
}

function filedSummary(
  email: Email,
  reviewCount: number,
): Pick<LaneChipSummary, 'value' | 'extra'> {
  const job = email.job_id
    ? email.job_name?.trim() || `Job #${email.job_id}`
    : null
  return { value: job, extra: Math.max(0, reviewCount) }
}

/**
 * Collapse the four lanes into their chip summaries.
 *
 * Inputs: the email plus the values the caller already resolved through the
 * canonical helpers (category label, active signals, review count).
 * Output: exactly four summaries in the lane order the expanded strip uses,
 * so the collapsed row and the open strip cannot disagree about ordering.
 * Failure mode: a group with nothing set yields value null and extra 0 — a
 * chip that still opens.
 */
export function buildLaneChipSummaries(
  email: Email,
  context: LaneSummaryContext,
): LaneChipSummary[] {
  const intelligence = selectIntelligenceSignals(email, {
    hasVerification: context.hasVerification,
    hasInvoice: context.hasInvoice,
  })
  return [
    { lane: 'company', label: 'Company', ...companySummary(email) },
    {
      lane: 'classification',
      label: 'Class',
      value: context.categoryLabel?.trim() || null,
      extra: 0,
    },
    {
      // Intel has no single headline value — it is a count of signals, so the
      // count IS the summary and printing one signal's text would mislead.
      lane: 'intelligence',
      label: 'Intel.',
      value: null,
      extra: intelligence.totalCount,
    },
    { lane: 'filedTo', label: 'Filed', ...filedSummary(email, context.reviewCount) },
  ]
}

/**
 * Same summaries, with the category label resolved the way the open lane
 * resolves it.
 *
 * Kept beside the pure builder so callers never hand-roll the category label:
 * ClassificationLane reads it through getCategoryConfig with the operator's
 * personal + org custom lists, and a chip that ignored those would print
 * "General" over a custom category.
 */
export function useLaneChipSummaries(
  email: Email,
  context: Omit<LaneSummaryContext, 'categoryLabel'>,
): LaneChipSummary[] {
  const personalCategories = usePersonalCategories()
  const orgCustomCategories = useOrgCustomCategories()
  const categoryLabel = email.category
    ? getCategoryConfig(email.category, [
        ...personalCategories,
        ...orgCustomCategories,
      ]).label
    : null
  return buildLaneChipSummaries(email, { ...context, categoryLabel })
}
